import {
  Row,
  Col,
  Card,
  Radio,
  Table,
  Button,
  Avatar,
  Typography,
  Tag,
  Flex,
  Modal,
  Input,
  Switch,
  Tooltip,
} from "antd";

// Images
import face2 from "shared/assets/images/face-2.jpg";
import { useAuth } from "shared/contexts/AuthProvider";
import { formatDateTime } from "shared/utils/datetime";
import { useEffect, useState } from "react";

// Refactored imports
import { useUserManagement } from "../hooks/useUserManagement";
import { getRoleColor } from "../constants";
import logger from "shared/utils/logging";

const { Title } = Typography;

const UsersManagement = () => {
  const onChange = (e) => logger.log(`radio checked:${e.target.value}`);

  const {
    isAuthenticated,
    loading: authLoading,
    isSuperUser,
    user: currentUser,
  } = useAuth();

  // Use the custom hook for user management
  const {
    usersData,
    loading,
    setUserPassword,
    setUserSuperAdmin,
    loadUsers,
    pagination,
  } = useUserManagement();

  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [superAdminUpdating, setSuperAdminUpdating] = useState(null); // username being updated

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadUsers();
    }
  }, [authLoading, isAuthenticated, loadUsers]);

  const { results = [] } = usersData;

  const handleSetPasswordClicked = (userId) => {
    setSelectedUserId(userId);
    setIsPasswordModalVisible(true);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handlePasswordSubmit = async () => {
    const success = await setUserPassword(selectedUserId, password);
    if (success) {
      setIsPasswordModalVisible(false);
      setPassword("");
    }
  };

  const handleModalCancel = () => {
    setIsPasswordModalVisible(false);
    setPassword("");
  };

  const handleSuperAdminToggle = (username, checked) => {
    Modal.confirm({
      title: checked
        ? `Grant Super Admin to "${username}"?`
        : `Revoke Super Admin from "${username}"?`,
      content: checked
        ? "This user will gain full administrative access to the system."
        : "This user will lose all administrative access.",
      okText: "Yes",
      okType: checked ? "primary" : "danger",
      cancelText: "No",
      onOk: async () => {
        setSuperAdminUpdating(username);
        try {
          return await setUserSuperAdmin(username, checked);
        } finally {
          setSuperAdminUpdating(null);
        }
      },
    });
  };

  const users = results;

  const columns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      width: "32%",
    },
    {
      title: "Organizations",
      dataIndex: "organizations",
      key: "organizations",
      width: "40%",
    },

    {
      title: "Is Super Admin",
      key: "permissions",
      dataIndex: "permissions",
    },
    {
      title: "Last Login At",
      key: "last_login_at",
      dataIndex: "last_login_at",
    },
    {
      title: "",
      key: "actions",
      dataIndex: "actions",
      render: (_, record) => (
        <Flex justify="space-between" align="center">
          <Button
            type="link"
            onClick={() => handleSetPasswordClicked(record.key)}
          >
            Set Password
          </Button>
        </Flex>
      ),
      width: "20%",
      fixed: "right",
    },
  ];

  const genUserData = (record) => {
    logger.log("genUserData record=", record);
    const user = record?.user;
    const organization_roles = record?.organization_roles || [];

    return {
      key: user.username,
      name: (
        <>
          <Avatar.Group>
            <Avatar
              className="shape-avatar"
              shape="square"
              size={40}
              src={face2}
            ></Avatar>
            <div className="avatar-info">
              <Title level={5}>{user.username}</Title>
              <p>{user.email}</p>
              <p>Fullname: {user.fullname}</p>
              {/* <p>Last login: {formatDateTime(user.last_login_at)}</p> */}
            </div>
          </Avatar.Group>{" "}
        </>
      ),
      organizations: (
        <>
          {organization_roles.length > 0 ? (
            organization_roles.map((orgRole) => (
              <div key={orgRole.organization.org_id} className="author-info">
                <Title level={5}>{orgRole.organization.name}: </Title>
                <span>
                  {orgRole.roles.map((role, index) => (
                    <Tag color={getRoleColor(role)} key={role}>
                      {role}
                    </Tag>
                  ))}
                </span>
              </div>
            ))
          ) : (
            <div className="author-info"></div>
          )}
        </>
      ),

      permissions: (() => {
        const isSelf = user.username === currentUser?.username;
        const toggle = (
          <Switch
            size="small"
            checked={user.is_superuser}
            disabled={!isSuperUser || isSelf}
            loading={superAdminUpdating === user.username}
            onChange={(checked) =>
              handleSuperAdminToggle(user.username, checked)
            }
            checkedChildren="Yes"
            unCheckedChildren="No"
          />
        );
        return isSelf ? (
          <Tooltip title="You cannot change your own Super Admin status">
            {toggle}
          </Tooltip>
        ) : (
          toggle
        );
      })(),
      last_login_at: (
        <>
          <div className="ant-employed" key={user.username}>
            <span>{formatDateTime(user.last_login_at)}</span>
          </div>
        </>
      ),
    };
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant="outlined"
              className="criclebox tablespace mb-24"
              title="Users"
              extra={
                <>
                  <Radio.Group onChange={onChange} defaultValue="a">
                    <Radio.Button value="a">All</Radio.Button>
                    <Radio.Button value="b">ONLINE</Radio.Button>
                  </Radio.Group>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={users.map((user) => genUserData(user))}
                  loading={loading}
                  pagination={{
                    ...pagination,
                    showSizeChanger: true,
                  }}
                  className="ant-border-space"
                />
              </div>
            </Card>

            {/* <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Projects Table"
              extra={
                <>
                  <Radio.Group onChange={onChange} defaultValue="all">
                    <Radio.Button value="all">All</Radio.Button>
                    <Radio.Button value="online">ONLINE</Radio.Button>
                    <Radio.Button value="store">STORES</Radio.Button>
                  </Radio.Group>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={project}
                  dataSource={dataproject}
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
              <div className="uploadfile pb-15 shadow-none">
                <Upload {...formProps}>
                  <Button
                    type="dashed"
                    className="ant-full-box"
                    icon={<ToTopOutlined />}
                  >
                    Click to Upload
                  </Button>
                </Upload>
              </div>
            </Card> */}
          </Col>
        </Row>
      </div>

      <Modal
        title="Set User Password"
        open={isPasswordModalVisible}
        onOk={handlePasswordSubmit}
        onCancel={handleModalCancel}
      >
        <Input.Password
          placeholder="Enter new password"
          value={password}
          onChange={handlePasswordChange}
        />
      </Modal>
    </>
  );
};

export default UsersManagement;
